<template>
  <div class="dashboard-header">
    <div class="header-content">
      <div class="level">
        <div class="level-left">
          <!-- Philly Logo -->
          <div class="logo-container">
            <img
              :src="deptLogo.src"
              :alt="deptLogo.alt"
              class="dept-logo"
            />
          </div>
        </div>
        <div class="level-right">
          <!-- User Profile Section with Initials -->
          <div v-if="currentUser" class="level-item">
            <div class="user-profile-wrapper" v-click-outside="closeUserDropdown">
              <div
                class="user-initials-circle"
                :class="{ 'is-warning': !isQbUserLinked }"
                @click="toggleUserDropdown"
                role="button"
                tabindex="0"
                @keydown.enter="toggleUserDropdown"
                @keydown.space.prevent="toggleUserDropdown"
              >
                <template v-if="isQbUserLinked">
                  {{ getUserInitials }}
                </template>
                <template v-else>
                  <i class="fas fa-exclamation-triangle"></i>
                </template>
              </div>

              <!-- User Dropdown -->
              <transition name="dropdown">
                <div v-if="showUserDropdown" class="user-dropdown">
                  <!-- Error state: qbUser not linked -->
                  <template v-if="!isQbUserLinked">
                    <div class="dropdown-item error-message-item">
                      <p class="error-message">Your SSO account hasn't been linked properly to Quickbase. Please contact a developer for help.</p>
                    </div>
                    <div class="dropdown-divider"></div>
                    <div class="dropdown-item logout-item" @click="handleLogout">
                      <span class="logout-icon">
                        <i class="fas fa-sign-out-alt"></i>
                      </span>
                      <span class="logout-text">Logout</span>
                    </div>
                  </template>

                  <!-- Normal state: show qbUser info -->
                  <template v-else>
                    <div class="dropdown-item user-info-item">
                      <p class="user-info-label">Name</p>
                      <p class="user-info-value">{{ currentUser.qbUser.full_name || 'User' }}</p>
                    </div>
                    <div class="dropdown-item user-info-item">
                      <p class="user-info-label">Email</p>
                      <p class="user-info-value">{{ currentUser.qbUser.email || 'N/A' }}</p>
                    </div>
                    <div class="dropdown-item user-info-item">
                      <p class="user-info-label">Role</p>
                      <p class="user-info-value">{{ userRoleLabel }}</p>
                    </div>
                    <div class="dropdown-divider"></div>
                    <div class="dropdown-item logout-item" @click="handleLogout">
                      <span class="logout-icon">
                        <i class="fas fa-sign-out-alt"></i>
                      </span>
                      <span class="logout-text">Logout</span>
                    </div>
                  </template>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import logo from '@/assets/DPD BELL logo_w.png';

export default {
  name: "DashboardHeader",
  props: {
    currentUser: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showUserDropdown: false,
      deptLogo: {
        src: logo,
        alt: 'Philadelphia Department of Planning and Development logo',
      },
    };
  },
  computed: {
    isQbUserLinked() {
      return !!this.currentUser?.qbUser?.rid;
    },
    userRole() {
      return this.currentUser?.qbUser?.role || null;
    },
    userRoleLabel() {
      const roleMap = {
        'property_owner': 'Property Owner',
        'ar': 'Authorized Representative',
      };
      return roleMap[this.userRole] || '';
    },
    getUserInitials() {
      const name = this.currentUser?.qbUser?.full_name;
      if (!name) return '?';

      const nameParts = name.trim().split(' ');
      if (nameParts.length === 1) {
        return nameParts[0].charAt(0).toUpperCase();
      }

      const firstInitial = nameParts[0].charAt(0).toUpperCase();
      const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
      return firstInitial + lastInitial;
    },
  },
  methods: {
    toggleUserDropdown() {
      this.showUserDropdown = !this.showUserDropdown;
    },
    closeUserDropdown() {
      this.showUserDropdown = false;
    },
    async handleLogout() {
      console.log("Logging out...");
      this.closeUserDropdown();
      try {
        // Sign out via Azure B2C
        await this.$store.dispatch('auth/signOut');
        this.$router.push('/login')
        // The auth store will handle redirect to login
      } catch (error) {
        console.error('Logout error:', error);
        // Fallback: redirect to login anyway
        this.$router.push('/login');
      }
    },
  },
  directives: {
    'click-outside': {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
};
</script>

<style scoped>
/* Dashboard Header - Full Width */
.dashboard-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  background: #0f4d90; /* Dark Ben Franklin Blue - Philly Style Guide */
  color: #ffffff;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  height: 70px;
  display: flex;
  align-items: center;
}

/* Header Content Wrapper */
.header-content {
  width: 100%;
  padding: 0 2rem 0 1rem;
}

.header-content .level {
  margin: 0;
}

.header-content .level-left {
  margin-left: 0;
}

/* Logo Container */
.logo-container {
  display: flex;
  align-items: center;
  margin-right: 1rem;
}

.dept-logo {
  max-width: 160px;
  width: 100%;
  height: auto;
  display: block;
}

/* User Profile Wrapper */
.user-profile-wrapper {
  position: relative;
}

/* User Initials Circle */
.user-initials-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #2176d2; /* Ben Franklin Blue - Philly Style Guide */
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Montserrat', sans-serif;
  user-select: none;
}

.user-initials-circle:hover {
  background: rgba(33, 118, 210, 0.8); /* Lighter Ben Franklin Blue */
  transform: scale(1.05);
}

.user-initials-circle:focus {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

/* Warning state for initials circle */
.user-initials-circle.is-warning {
  background: #f3c613; /* Bell Yellow - Philly Style Guide */
  color: #444444; /* Dark Gray for contrast */
}

.user-initials-circle.is-warning:hover {
  background: rgba(243, 198, 19, 0.8);
}

/* User Dropdown */
.user-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  overflow: hidden;
  z-index: 1002;
}

.dropdown-item {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f0f0f0; /* Ghost Gray - Philly Style Guide */
}

.dropdown-item:last-child {
  border-bottom: none;
}

.user-info-item {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
}

.user-info-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
}

.user-info-value {
  font-size: 0.85rem;
  color: #000000;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  word-break: break-all;
  line-height: 1.3;
}

/* Error message in dropdown */
.error-message-item {
  padding: 1rem 0.75rem;
}

.error-message {
  font-size: 0.85rem;
  color: #cc3000; /* Love Park Red - Philly Style Guide */
  margin: 0;
  line-height: 1.4;
  font-family: 'Open Sans', sans-serif;
}

/* Dropdown Divider */
.dropdown-divider {
  height: 1px;
  background: #f0f0f0; /* Ghost Gray - Philly Style Guide */
  margin: 0;
}

/* Logout Item */
.logout-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: background 0.2s ease;
  padding: 0.5rem 0.75rem;
}

.logout-item:hover {
  background: rgba(204, 48, 0, 0.05); /* Love Park Red tint - Philly Style Guide */
}

.logout-icon {
  color: #cc3000; /* Love Park Red - Philly Style Guide */
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.logout-text {
  font-size: 0.85rem;
  font-weight: 600;
  color: #cc3000; /* Love Park Red - Philly Style Guide */
  font-family: 'Open Sans', sans-serif;
}

/* Dropdown Transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-header {
    padding: 1rem 0;
    height: 60px;
  }

  .header-content {
    padding: 0 1rem 0 0.5rem;
  }

  .dept-logo {
    max-width: 120px;
  }

  .user-initials-circle {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }

  .user-dropdown {
    min-width: 220px;
  }
}
</style>
